import React, { useCallback, useMemo } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { createSellerCardData, getSellerCategoryOptions } from '../../util/sellerCardData';

import FallbackPage from './FallbackPage';
import {
  ASSET_NAME,
  LANDING_SELLERS_PAGE_SIZE,
  searchLandingSellers,
} from './LandingPage.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';
import { isApprovedSeller } from '../../util/userHelpers';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    sellers = [],
    sellerSearchInProgress,
    sellerSearchError,
    onSearchLandingSellers,
    currentUser,
  } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const isCurrentUserSeller = isApprovedSeller(currentUser);
  const sellerCards = sellers
    .map(seller => createSellerCardData(seller, config.user?.userFields, routeConfiguration))
    .filter(seller => seller != null);
  const sellerCategoryOptions = getSellerCategoryOptions(config.user?.userFields);
  const onSearch = useCallback(
    searchParams =>
      onSearchLandingSellers({
        searchParams: {
          ...searchParams,
          page: 1,
          perPage: LANDING_SELLERS_PAGE_SIZE,
        },
        config,
      }),
    [config, onSearchLandingSellers]
  );
  const landingSellers = useMemo(
    () => ({
      sellerCards,
      categoryOptions: sellerCategoryOptions,
      searchInProgress: sellerSearchInProgress,
      searchError: sellerSearchError,
      onSearch,
    }),
    [onSearch, sellerCards, sellerCategoryOptions, sellerSearchError, sellerSearchInProgress]
  );

  return (
    <PageBuilder
      pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      featuredListings={getFeaturedListingsProps(camelize(ASSET_NAME), props)}
      landingSellers={landingSellers}
      isCurrentUserSeller={isCurrentUserSeller}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};
  const {
    sellerResultRefs = [],
    sellerSearchInProgress = false,
    sellerSearchError = null,
  } = state.LandingPage || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);
  const sellers = getMarketplaceEntities(state, sellerResultRefs);

  return {
    pageAssetsData,
    featuredListingData,
    getListingEntitiesById,
    currentUser: state.user.currentUser,
    inProgress,
    error,
    sellers,
    sellerSearchInProgress,
    sellerSearchError,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
  onSearchLandingSellers: params => dispatch(searchLandingSellers(params)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
